import React, { useState } from 'react';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Edit, Trash2, Eye } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';

/**
 * Reusable inline action bar for list cards:
 * - Publish/Unpublish switch (PATCH /api/admin/{collection}/{id}/publish)
 * - Preview button (calls onPreview)
 * - Edit / Delete buttons (call onEdit / onDelete)
 */
export const ItemActions = ({
  collection,
  item,
  onChanged,
  onEdit,
  onDelete,
  onPreview,
  size = 'sm',
}) => {
  const [updating, setUpdating] = useState(false);
  const { toast } = useToast();

  const togglePublished = async () => {
    setUpdating(true);
    const next = !(item.published !== false);
    try {
      await adminApi.patch(`/${collection}/${item.id}/publish`, { published: next });
      toast({
        title: next ? 'Published' : 'Unpublished',
        description: `Now ${next ? 'visible' : 'hidden'} on the public site.`,
      });
      onChanged?.();
    } catch (err) {
      toast({ title: 'Error', description: 'Could not change publish state', variant: 'destructive' });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2 pr-2 border-r border-gray-200" title="Publish on site">
        <Switch
          checked={item.published !== false}
          onCheckedChange={togglePublished}
          disabled={updating}
          data-testid={`publish-toggle-${collection}-${item.id}`}
        />
        <span className="text-xs text-gray-500">{item.published === false ? 'Draft' : 'Live'}</span>
      </div>
      {onPreview && (
        <Button size={size} variant="outline" onClick={() => onPreview(item)} title="Preview" data-testid={`preview-${collection}-${item.id}`}>
          <Eye className="w-4 h-4" />
        </Button>
      )}
      {onEdit && (
        <Button size={size} variant="outline" onClick={() => onEdit(item)} title="Edit">
          <Edit className="w-4 h-4" />
        </Button>
      )}
      {onDelete && (
        <Button size={size} variant="destructive" onClick={() => onDelete(item.id)} title="Delete">
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

export default ItemActions;
